// «Лендинги»: страницы офферов на своих доменах (этап 3). Версия неизменяема: поправили данные →
// «Собрать версию» → «Опубликовать» на адрес домена (одна версия или A/B с тем, что уже там открыто).
// Цифры версии — за 7 дней: визиты, лиды, конверсия, апрувы и скорость страницы у покупателей (медиана и 75%
// главного экрана и ответа сервера — замеры с самих лендингов). Агент публикует через «Решения».
// Адрес оффера — /market/<slug>/ (на него настроена конверсия в Meta); переключатель «браузер подставляет номер»
// — для A/B: версия без подстановки рядом с обычной (заявка #5).
import {
  Button,
  Card,
  Chip,
  EmptyState,
  int,
  pct,
  plural,
  Skeleton,
  type Tone,
  toast,
  when,
} from '@autoaff/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Globe, Hammer, Plus, Send } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { api } from '../api';
import type { OffersData } from '../business';
import { OfferTabs, PageHead } from './common';

interface Domain {
  id: number;
  host: string;
  status: 'active' | 'paused' | 'retired';
  metaVerified: boolean;
  offerId: number | null;
}
interface Version {
  id: number;
  version: number;
  note: string | null;
  createdBy: string;
  createdAt: string;
  visits: number;
  leads: number;
  approved: number;
  phoneAutofill: boolean;
  /** Скорость у покупателей за 7 дней, мс (null — замеров нет). */
  speed?: Speed | null;
}
interface Speed {
  samples: number;
  lcpP50: number | null;
  lcpP75: number | null;
  ttfbP50: number | null;
  ttfbP75: number | null;
}
interface Landing {
  id: number;
  offerId: number;
  offerName: string;
  /** Путь для публикации: /market/<прежний slug> — на него настроена конверсия оффера. */
  marketPath: string;
  name: string;
  data: Record<string, unknown>;
  lang: string;
  createdBy: string;
  updatedAt: string;
  versions: Version[];
}
interface Route {
  id: number;
  host: string;
  path: string;
  active: boolean;
  variants: Array<{ versionId: number; weight: number }>;
  /** A/B: цифры с момента последней смены долей и вывод (есть только при нескольких версиях). */
  ab?: {
    since: string;
    variants: Array<{
      versionId: number;
      visits: number;
      leads: number;
      approved: number;
      decided: number;
      cr: number | null;
      crLow: number | null;
      crHigh: number | null;
      approveRate: number | null;
      probBest: number;
    }>;
    verdict: { status: 'collecting' | 'leading' | 'winner' | 'even'; leader: number | null; text: string };
  };
}
interface LandingsData {
  domains: Domain[];
  landings: Landing[];
  routes: Route[];
}

const KEY = ['landings'];
/** Адрес, как его откроет посетитель: не корень — со «/» на конце. */
const shownPath = (path: string) => (path === '/' ? '' : `${path}/`);
const DOMAIN_STATUS: Record<Domain['status'], { label: string; tone: 'success' | 'warning' | 'default' }> = {
  active: { label: 'Работает', tone: 'success' },
  paused: { label: 'На паузе', tone: 'warning' },
  retired: { label: 'Выведен', tone: 'default' },
};

function useLandings() {
  return useQuery({ queryKey: KEY, queryFn: () => api<LandingsData>('/api/landings') });
}

/** Опубликовать версию: адрес на активном домене; «A/B» — вместе с тем, что уже открыто по адресу. */
function PublishForm({
  version,
  marketPath,
  data,
  onDone,
}: {
  version: Version;
  marketPath: string;
  data: LandingsData;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const active = data.domains.filter((d) => d.status === 'active');
  const [host, setHost] = useState(active[0]?.host ?? '');
  const [path, setPath] = useState(`${marketPath}/`);
  const [ab, setAb] = useState(false);
  const [busy, setBusy] = useState(false);
  const norm = `/${path.split('/').filter(Boolean).join('/')}`;
  const current = data.routes.find((r) => r.active && r.host === host && r.path === norm);
  const others = current?.variants.filter((v) => v.versionId !== version.id) ?? [];

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      // A/B — поровну между уже открытыми версиями и этой
      const variants =
        ab && others.length
          ? [...others.map((v) => ({ ...v, weight: 1 })), { versionId: version.id, weight: 1 }]
          : [{ versionId: version.id, weight: 1 }];
      await api('/api/landings/routes', { host, path: norm, variants }, 'PUT');
      await qc.invalidateQueries({ queryKey: KEY });
      toast(
        ab && others.length
          ? `A/B на ${host}${shownPath(norm)}: поровну между ${variants.length} версиями`
          : `Опубликовано: ${host}${shownPath(norm)}`,
      );
      onDone();
    } catch (err) {
      toast((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (!active.length)
    return <p className="muted landing-note">Сначала добавьте домен ниже — публиковать пока некуда.</p>;
  return (
    <form className="landing-publish" onSubmit={submit}>
      <label className="label" htmlFor={`pub-host-${version.id}`}>
        Домен
      </label>
      <select
        id={`pub-host-${version.id}`}
        className="input"
        value={host}
        onChange={(e) => setHost(e.target.value)}
      >
        {active.map((d) => (
          <option key={d.id} value={d.host}>
            {d.host}
          </option>
        ))}
      </select>
      <label className="label" htmlFor={`pub-path-${version.id}`}>
        Путь
      </label>
      <input
        id={`pub-path-${version.id}`}
        className="input mono"
        value={path}
        onChange={(e) => setPath(e.target.value)}
        placeholder={`${marketPath}/`}
      />
      <p className="muted landing-note">
        Для оффера — {marketPath}/: на этот адрес настроена конверсия в Meta, она засчитает заявки и на новом
        домене.
      </p>
      {others.length > 0 && (
        <label className="landing-ab">
          <input type="checkbox" checked={ab} onChange={(e) => setAb(e.target.checked)} />
          <span>
            A/B с тем, что уже открыто по этому адресу (
            {others.length === 1 ? '1 версия' : `${others.length} версии`}) — посетители разделятся поровну
          </span>
        </label>
      )}
      <div className="row">
        <Button type="submit" variant="primary" icon={<Send />} disabled={busy || !host}>
          {busy ? 'Публикую…' : 'Опубликовать'}
        </Button>
        <Button variant="ghost" onClick={onDone}>
          Отмена
        </Button>
      </div>
    </form>
  );
}

function LandingCard({ l, data }: { l: Landing; data: LandingsData }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [publish, setPublish] = useState<number | null>(null);
  const autofill = l.data.phoneAutofill !== false;
  const liveOn = (versionId: number) =>
    data.routes.filter((r) => r.active && r.variants.some((v) => v.versionId === versionId));

  const setAutofill = async (on: boolean) => {
    setBusy(true);
    try {
      await api(`/api/landings/${l.id}`, { data: { ...l.data, phoneAutofill: on } }, 'PATCH');
      await qc.invalidateQueries({ queryKey: KEY });
      toast('Сохранено — соберите версию, чтобы изменение попало на страницу');
    } catch (err) {
      toast((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const build = async () => {
    setBusy(true);
    try {
      const r = await api<{ version: number; created: boolean }>(`/api/landings/${l.id}/versions`, {});
      await qc.invalidateQueries({ queryKey: KEY });
      toast(r.created ? `Собрана версия ${r.version}` : `Данные не менялись — это версия ${r.version}`);
    } catch (err) {
      toast((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title={
        <span className="landing-title">
          {l.name} <span className="muted">· {l.offerName}</span>
        </span>
      }
      action={
        <Button size="sm" icon={<Hammer />} onClick={build} disabled={busy}>
          {busy ? 'Собираю…' : 'Собрать версию'}
        </Button>
      }
    >
      <p className="muted landing-note">
        Язык: {l.lang === 'uz' ? 'узбекский' : 'русский'} ·{' '}
        {l.createdBy === 'agent' ? 'сделал агент' : 'сделали вы'} · изменён {when(l.updatedAt)}
      </p>
      <label className="landing-ab landing-note">
        <input
          type="checkbox"
          checked={autofill}
          disabled={busy}
          onChange={(e) => void setAutofill(e.target.checked)}
        />
        <span>
          Браузер может подставлять номер телефона. Выключите, соберите версию и опубликуйте её рядом с
          обычной — A/B покажет, у какой версии лучше апрув.
        </span>
      </label>
      {l.versions.length === 0 ? (
        <p className="muted landing-note">Версий ещё нет — соберите первую.</p>
      ) : (
        <table className="tbl landing-versions">
          <thead>
            <tr>
              <th>Версия</th>
              <th className="num">Визиты</th>
              <th className="num">Лиды</th>
              <th className="num" data-m="hide">
                Конверсия
              </th>
              <th className="num">Апрувы</th>
              <th aria-label="Действия" />
            </tr>
          </thead>
          <tbody>
            {l.versions.map((v) => {
              const live = liveOn(v.id);
              return (
                <tr key={v.id}>
                  <td>
                    <b>v{v.version}</b>
                    <span className="sub">
                      {when(v.createdAt)}
                      {v.createdBy === 'agent' ? ' · агент' : ''}
                      {v.phoneAutofill ? '' : ' · без подстановки номера'}
                    </span>
                    {/* На телефоне столбцы с цифрами скрыты — те же цифры одной строкой, с подписями */}
                    <span className="sub landing-mstats">
                      {int(v.visits)} {plural(v.visits, 'визит', 'визита', 'визитов')} · {int(v.leads)}{' '}
                      {plural(v.leads, 'лид', 'лида', 'лидов')}
                      {v.visits ? ` · ${pct((v.leads / v.visits) * 100)}` : ''} · {int(v.approved)}{' '}
                      {plural(v.approved, 'апрув', 'апрува', 'апрувов')}
                    </span>
                    {live.length > 0 && (
                      <span className="sub">
                        {live.map((r) => (
                          <Chip key={r.id} tone="success" dot>
                            {r.host}
                            {shownPath(r.path)}
                          </Chip>
                        ))}
                      </span>
                    )}
                    {publish === v.id && (
                      <PublishForm
                        version={v}
                        marketPath={l.marketPath}
                        data={data}
                        onDone={() => setPublish(null)}
                      />
                    )}
                  </td>
                  <td className="num" data-m="hide">
                    {int(v.visits)}
                  </td>
                  <td className="num" data-m="hide">
                    {int(v.leads)}
                  </td>
                  <td className="num" data-m="hide">
                    {v.visits ? pct((v.leads / v.visits) * 100) : '—'}
                  </td>
                  <td className="num" data-m="hide">
                    {int(v.approved)}
                  </td>
                  <td className="landing-acts">
                    <a
                      className="btn btn-sm btn-ghost"
                      href={`/api/landings/versions/${v.id}/preview`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <ExternalLink aria-hidden="true" />
                      Смотреть
                    </a>
                    {publish !== v.id && (
                      <Button size="sm" variant="ghost" icon={<Send />} onClick={() => setPublish(v.id)}>
                        Опубликовать
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <SpeedBlock versions={l.versions} />
    </Card>
  );
}

/** Меньше стольких замеров — оценки нет: цифры случайны. */
const SPEED_MIN_SAMPLES = 20;
const secs = (ms: number | null) => (ms === null ? '—' : `${(ms / 1000).toFixed(1)} с`);

/** Оценка по главному экрану у 75% покупателей: до 2.5 с — быстро, до 4 с — средне (пороги Google). */
function speedVerdict(s: Speed): { tone: Tone; label: string } {
  if (s.samples < SPEED_MIN_SAMPLES || s.lcpP75 === null) return { tone: 'default', label: 'мало замеров' };
  if (s.lcpP75 <= 2500) return { tone: 'success', label: 'быстро' };
  if (s.lcpP75 <= 4000) return { tone: 'warning', label: 'средне' };
  return { tone: 'danger', label: 'медленно' };
}

/** Скорость страницы у настоящих покупателей за 7 дней — по версиям, у которых есть замеры. */
function SpeedBlock({ versions }: { versions: Version[] }) {
  const rows = versions.flatMap((v) => (v.speed ? [{ v, s: v.speed }] : []));
  if (!rows.length) return null;
  return (
    <div className="speed">
      <div className="speed-head">
        <b>Скорость у покупателей</b>
        <span className="muted">за 7 дней</span>
      </div>
      <table className="speed-tbl">
        <thead>
          <tr>
            <th>Версия</th>
            <th className="num">Экран, медиана</th>
            <th className="num">Экран, у 75%</th>
            <th className="num">Сервер, медиана</th>
            <th className="num">Сервер, у 75%</th>
            <th>Оценка</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ v, s }) => {
            const verdict = speedVerdict(s);
            return (
              <tr key={v.id}>
                <td className="speed-name">
                  <b>v{v.version}</b>{' '}
                  <span className="muted">
                    {int(s.samples)} {plural(s.samples, 'замер', 'замера', 'замеров')}
                  </span>
                </td>
                <td className="num" data-label="экран, медиана">
                  {secs(s.lcpP50)}
                </td>
                <td className="num" data-label="экран, у 75%">
                  {secs(s.lcpP75)}
                </td>
                <td className="num" data-label="сервер, медиана">
                  {secs(s.ttfbP50)}
                </td>
                <td className="num" data-label="сервер, у 75%">
                  {secs(s.ttfbP75)}
                </td>
                <td className="speed-verdict">
                  <Chip tone={verdict.tone} dot={verdict.tone !== 'default'}>
                    {verdict.label}
                  </Chip>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="speed-note">
        Экран — когда покупатель увидел фото и цену: хорошо, если у 75% покупателей быстрее 2.5 с. Сервер — от
        нажатия на объявление до начала загрузки страницы: хорошо — до 0.8 с.
      </p>
    </div>
  );
}

const pct1 = (n: number | null) => (n === null ? '—' : `${(n * 100).toFixed(1)}%`);
const AB_TONE = { winner: 'success', leading: 'accent', even: 'default', collecting: 'default' } as const;
const AB_LABEL = {
  winner: 'Есть победитель',
  leading: 'Есть лидер',
  even: 'Пока равны',
  collecting: 'Копим данные',
};

/** A/B по адресу: конверсия с интервалом, апрув, вероятность «лучшая» и вывод словами. */
function AbBlock({ ab, name }: { ab: NonNullable<Route['ab']>; name: (id: number) => string }) {
  return (
    <div className="ab">
      <div className="ab-head">
        <Chip tone={AB_TONE[ab.verdict.status]} dot>
          {AB_LABEL[ab.verdict.status]}
        </Chip>
        <span className="muted">считаем с {when(ab.since)}</span>
      </div>
      <table className="ab-tbl">
        <thead>
          <tr>
            <th>Версия</th>
            <th className="num">Визиты</th>
            <th className="num">Лиды</th>
            <th className="num">Конверсия</th>
            <th className="num">Апрув</th>
            <th>Шанс быть лучшей</th>
          </tr>
        </thead>
        <tbody>
          {ab.variants.map((v) => (
            <tr key={v.versionId} className={ab.verdict.leader === v.versionId ? 'lead' : undefined}>
              <td className="ab-name">{name(v.versionId)}</td>
              <td className="num" data-label="визитов">
                {int(v.visits)}
              </td>
              <td className="num" data-label="лидов">
                {int(v.leads)}
              </td>
              <td
                className="num"
                data-label="конверсия"
                title={v.crLow !== null ? `95%: ${pct1(v.crLow)} … ${pct1(v.crHigh)}` : undefined}
              >
                {pct1(v.cr)}
              </td>
              <td className="num" data-label="апрув">
                {pct1(v.approveRate)}
              </td>
              <td className="ab-prob-cell" data-label="шанс быть лучшей">
                <div className="ab-prob">
                  <div className="meter" aria-hidden="true">
                    <i style={{ width: `${Math.round(v.probBest * 100)}%` }} />
                  </div>
                  <span className="tnum">{Math.round(v.probBest * 100)}%</span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="ab-verdict">{ab.verdict.text}</p>
    </div>
  );
}

function RoutesCard({ data }: { data: LandingsData }) {
  const qc = useQueryClient();
  const versions = new Map(data.landings.flatMap((l) => l.versions.map((v) => [v.id, { l, v }] as const)));
  const live = data.routes.filter((r) => r.active);
  const unpublish = async (id: number) => {
    try {
      await api(`/api/landings/routes/${id}`, undefined, 'DELETE');
      await qc.invalidateQueries({ queryKey: KEY });
      toast('Адрес снят — страница больше не открывается');
    } catch (err) {
      toast((err as Error).message);
    }
  };
  return (
    <Card title="Открытые адреса" count={live.length}>
      {live.length === 0 ? (
        <p className="muted landing-note">Ни один лендинг пока не опубликован.</p>
      ) : (
        <ul className="landing-routes">
          {live.map((r) => {
            const total = r.variants.reduce((s, v) => s + v.weight, 0) || 1;
            return (
              <li key={r.id}>
                <div className="min-w-0">
                  <b className="mono">
                    {r.host}
                    {shownPath(r.path)}
                  </b>
                  <span>
                    {r.variants
                      .map((v) => {
                        const x = versions.get(v.versionId);
                        const name = x ? `${x.l.name} v${x.v.version}` : `версия ${v.versionId}`;
                        return r.variants.length > 1
                          ? `${name} — ${Math.round((v.weight / total) * 100)}%`
                          : name;
                      })
                      .join(' · ')}
                  </span>
                </div>
                <Button size="sm" variant="ghost" onClick={() => unpublish(r.id)}>
                  Снять
                </Button>
                {r.ab && (
                  <AbBlock
                    ab={r.ab}
                    name={(id) => {
                      const x = versions.get(id);
                      return x ? `${x.l.name} v${x.v.version}` : `версия ${id}`;
                    }}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function DomainsCard({ data }: { data: LandingsData }) {
  const qc = useQueryClient();
  const [host, setHost] = useState('');
  const [busy, setBusy] = useState(false);
  const add = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api('/api/landings/domains', { host });
      setHost('');
      await qc.invalidateQueries({ queryKey: KEY });
      toast('Домен добавлен — сертификат выпустится при первом заходе');
    } catch (err) {
      toast((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const setStatus = async (d: Domain, status: Domain['status']) => {
    try {
      await api(`/api/landings/domains/${d.id}`, { status }, 'PATCH');
      await qc.invalidateQueries({ queryKey: KEY });
    } catch (err) {
      toast((err as Error).message);
    }
  };
  return (
    <Card title="Домены" count={data.domains.length}>
      {data.domains.length > 0 && (
        <ul className="landing-routes">
          {data.domains.map((d) => (
            <li key={d.id}>
              <div className="min-w-0">
                <b className="mono">{d.host}</b>
                <span>
                  <Chip tone={DOMAIN_STATUS[d.status].tone} dot={d.status === 'active'}>
                    {DOMAIN_STATUS[d.status].label}
                  </Chip>{' '}
                  {d.metaVerified ? (
                    <Chip tone="success">подтверждён в Meta</Chip>
                  ) : (
                    <Chip>не подтверждён в Meta</Chip>
                  )}
                </span>
              </div>
              {d.status === 'active' ? (
                <Button size="sm" variant="ghost" onClick={() => setStatus(d, 'paused')}>
                  На паузу
                </Button>
              ) : (
                <Button size="sm" variant="ghost" onClick={() => setStatus(d, 'active')}>
                  Включить
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      <form className="landing-domain-form" onSubmit={add}>
        <label className="label" htmlFor="new-domain">
          Новый домен
        </label>
        <div className="pb-row">
          <input
            id="new-domain"
            className="input mono"
            placeholder="shop-uz.com"
            value={host}
            onChange={(e) => setHost(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
          <Button type="submit" icon={<Plus />} disabled={busy || host.trim().length < 4}>
            Добавить домен
          </Button>
        </div>
        <p className="muted landing-note">
          Направьте домен на сервер (запись A в DNS). Сертификат выдаётся только доменам из этого списка.
          Новые объявления ведите на новые домены.
        </p>
      </form>
    </Card>
  );
}

/** Новый лендинг из данных оффера: остальное (фото, тикер, пиксель) — правкой данных позже или агентом. */
function NewLanding({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const offers = useQuery({ queryKey: ['offers'], queryFn: () => api<OffersData>('/api/offers') });
  const mine = offers.data?.mine ?? [];
  const [offerId, setOfferId] = useState<number | ''>('');
  const [title, setTitle] = useState('');
  const [priceNew, setPriceNew] = useState('');
  const [priceOld, setPriceOld] = useState('');
  const [images, setImages] = useState('');
  const [busy, setBusy] = useState(false);
  const offer = mine.find((o) => o.id === offerId);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = {
        title: title.trim(),
        priceNew: Number(priceNew.replace(/\D/g, '')),
        ...(priceOld.trim() ? { priceOld: Number(priceOld.replace(/\D/g, '')) } : {}),
        images: images
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean)
          .map((src) => ({ src })),
      };
      await api('/api/landings', { offerId, name: offer?.name ?? title, lang: 'uz', data });
      await qc.invalidateQueries({ queryKey: KEY });
      toast('Лендинг создан — соберите версию и опубликуйте');
      onDone();
    } catch (err) {
      toast((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Новый лендинг">
      <form className="landing-new" onSubmit={submit}>
        <label className="label" htmlFor="nl-offer">
          Оффер
        </label>
        <select
          id="nl-offer"
          className="input"
          value={offerId}
          onChange={(e) => {
            const id = Number(e.target.value);
            setOfferId(id);
            const o = mine.find((x) => x.id === id);
            if (o && !title) setTitle(o.name);
            if (o?.priceUzs && !priceNew) setPriceNew(String(o.priceUzs));
          }}
          required
        >
          <option value="">Выберите оффер</option>
          {mine.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
        <label className="label" htmlFor="nl-title">
          Заголовок на странице (на узбекском)
        </label>
        <input
          id="nl-title"
          className="input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />
        <div className="landing-prices">
          <div>
            <label className="label" htmlFor="nl-price">
              Цена, сум
            </label>
            <input
              id="nl-price"
              className="input num"
              inputMode="numeric"
              value={priceNew}
              onChange={(e) => setPriceNew(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="label" htmlFor="nl-old">
              Старая цена, сум
            </label>
            <input
              id="nl-old"
              className="input num"
              inputMode="numeric"
              value={priceOld}
              onChange={(e) => setPriceOld(e.target.value)}
            />
          </div>
        </div>
        <label className="label" htmlFor="nl-images">
          Фото — адреса https://, по одному в строке
        </label>
        <textarea
          id="nl-images"
          className="input mono"
          rows={3}
          value={images}
          onChange={(e) => setImages(e.target.value)}
        />
        <div className="row">
          <Button
            type="submit"
            variant="primary"
            disabled={busy || !offerId || !title.trim() || !priceNew.trim()}
          >
            {busy ? 'Создаю…' : 'Создать лендинг'}
          </Button>
          <Button variant="ghost" onClick={onDone}>
            Отмена
          </Button>
        </div>
      </form>
    </Card>
  );
}

export function LandingsPage() {
  const q = useLandings();
  const [creating, setCreating] = useState(false);
  const d = q.data;
  return (
    <>
      <PageHead
        title="Лендинги"
        sub="Страницы офферов на своих доменах: версии, публикация и цифры каждой версии за 7 дней"
        actions={
          !creating && (
            <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
              Новый лендинг
            </Button>
          )
        }
      />
      <OfferTabs active="/landings" />
      <div className="pc pc-read">
        {creating && <NewLanding onDone={() => setCreating(false)} />}
        {q.isPending && (
          <Card>
            <Skeleton h={120} />
          </Card>
        )}
        {q.isError && (
          <EmptyState icon={<Globe />} title="Не удалось загрузить лендинги">
            {(q.error as Error).message}
          </EmptyState>
        )}
        {d && d.landings.length === 0 && !creating && (
          <EmptyState icon={<Globe />} title="Лендингов пока нет">
            Лендинг собирается из данных оффера: заголовок, цена, фото. Нажмите «Новый лендинг» — или
            попросите агента сделать его по офферу.
          </EmptyState>
        )}
        {d?.landings.map((l) => (
          <LandingCard key={l.id} l={l} data={d} />
        ))}
        {d && <RoutesCard data={d} />}
        {d && <DomainsCard data={d} />}
      </div>
    </>
  );
}
